import { useEffect, useMemo, useRef, useState } from "react";
import { Circle, CircleMarker, GeoJSON, MapContainer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import type { Feature, Geometry } from "geojson";

import { LatLng } from "../../utils/geocode";
import {
  DistrictFeatureCollection,
  DistrictProperties,
  loadAreasPolygons,
} from "../../utils/postcodeDistricts";
import { CoverageDistrictState } from "./types";
import LoadingWheel from "../shared/LoadingWheel";
import MapTileLayer from "../shared/MapTileLayer";

const MILES_TO_METRES = 1609.344;
const UK_CENTRE: LatLng = [54.5, -3.0];

interface CoverageMapProps {
  /** Yard location. When omitted the map fits to the loaded polygons instead. */
  centre?: LatLng;
  /** Radius circle around the yard; omitted = no circle. */
  radiusMiles?: number;
  districts: CoverageDistrictState[];
  /** Extra postcode areas to load polygons for even if none of their districts are in `districts`. */
  extraAreas?: string[];
  /** District to outline as the current selection. */
  focused?: string;
  onSelect: (district: string) => void;
  className?: string;
  /** Legend text for the two blue shades; defaults suit the onboarding wizard. */
  legend?: { covered: string; implicit?: string; excluded: string; outside: string };
}

/** Re-fit the map whenever the circle changes. */
function FitToRadius({ centre, radiusMiles }: { centre: LatLng; radiusMiles: number }) {
  const map = useMap();
  useEffect(() => {
    const bounds = L.latLng(centre).toBounds(radiusMiles * MILES_TO_METRES * 2);
    map.fitBounds(bounds, { padding: [20, 20] });
    setTimeout(() => map.invalidateSize(), 50);
  }, [map, centre, radiusMiles]);
  return null;
}

/** Fit to the union of loaded polygons once per set of areas (used when there's no radius). */
function FitToPolygons({ polygons }: { polygons?: Record<string, DistrictFeatureCollection> }) {
  const map = useMap();
  const fittedFor = useRef<string>();
  useEffect(() => {
    if (!polygons) return;
    const key = Object.keys(polygons).sort().join(",");
    if (fittedFor.current === key) return;
    fittedFor.current = key;

    let bounds: L.LatLngBounds | undefined;
    Object.values(polygons).forEach((fc) => {
      const b = L.geoJSON(fc as any).getBounds();
      if (!b.isValid()) return;
      bounds = bounds ? bounds.extend(b) : b;
    });
    if (bounds) map.fitBounds(bounds, { padding: [20, 20] });
    setTimeout(() => map.invalidateSize(), 50);
  }, [map, polygons]);
  return null;
}

const STYLE_SELECTED: L.PathOptions = { color: "#1d4ed8", weight: 1.5, fillColor: "#3b82f6", fillOpacity: 0.4 };
const STYLE_IMPLICIT: L.PathOptions = { color: "#1d4ed8", weight: 1, fillColor: "#60a5fa", fillOpacity: 0.18 };
const STYLE_DESELECTED: L.PathOptions = { color: "#b91c1c", weight: 1.5, fillColor: "#ef4444", fillOpacity: 0.2, dashArray: "4 3" };
const STYLE_OUTSIDE: L.PathOptions = { color: "#9ca3af", weight: 0.8, fillColor: "#e5e7eb", fillOpacity: 0.15 };
const STYLE_PENDING: L.PathOptions = { weight: 3, color: "#7c3aed" };
const STYLE_FOCUSED: L.PathOptions = { weight: 4, color: "#111827" };

/**
 * Shaded postcode-district polygons for every AREA touched by `districts` (plus `extraAreas`).
 * Blue = covered (solid: explicit price, light: via group base price), red-dashed = excluded,
 * grey = not in the list (outside radius / not covered). Purple outline = unsaved change, black = focused.
 */
export default function CoverageMap({
  centre,
  radiusMiles,
  districts,
  extraAreas,
  focused,
  onSelect,
  className,
  legend,
}: CoverageMapProps) {
  const [polygons, setPolygons] = useState<Record<string, DistrictFeatureCollection>>();
  const [error, setError] = useState<string>();

  const areas = useMemo(
    () => Array.from(new Set([...districts.map((d) => d.area), ...(extraAreas ?? [])])).filter(Boolean).sort(),
    [districts, extraAreas]
  );
  const areasKey = areas.join(",");

  const byDistrict = useMemo(() => {
    const map = new Map<string, CoverageDistrictState>();
    districts.forEach((d) => map.set(d.district, d));
    return map;
  }, [districts]);

  useEffect(() => {
    let cancelled = false;
    setError(undefined);
    if (areas.length === 0) {
      setPolygons({});
      return;
    }
    loadAreasPolygons(areas)
      .then((p) => !cancelled && setPolygons(p))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areasKey]);

  // Re-mount GeoJSON layers when anything that affects styling changes (react-leaflet's GeoJSON is static).
  const styleKey = useMemo(
    () =>
      `${focused ?? ""}#` +
      districts.map((d) => `${d.district}:${d.selected ? 1 : 0}${d.implicit ? "i" : ""}${d.pending ? "p" : ""}`).join("|"),
    [districts, focused]
  );

  const style = (feature?: Feature<Geometry, DistrictProperties>): L.PathOptions => {
    const name = feature?.properties.name;
    const state = name ? byDistrict.get(name) : undefined;
    let base: L.PathOptions = STYLE_OUTSIDE;
    if (state) base = !state.selected ? STYLE_DESELECTED : state.implicit ? STYLE_IMPLICIT : STYLE_SELECTED;
    if (state?.pending) base = { ...base, ...STYLE_PENDING };
    if (name && name === focused) base = { ...base, ...STYLE_FOCUSED };
    return base;
  };

  const onEachFeature = (feature: Feature<Geometry, DistrictProperties>, layer: L.Layer) => {
    const name = feature.properties.name;
    const state = byDistrict.get(name);
    const status = !state
      ? legend?.outside ?? "outside radius"
      : !state.selected
      ? legend?.excluded ?? "not covered"
      : state.implicit
      ? legend?.implicit ?? "covered (group price)"
      : legend?.covered ?? "covered";
    const town = state?.town ? ` · ${state.town}` : "";
    const price = state?.selected ? ` · £${state.price}` : "";
    layer.bindTooltip(`${name}${town} — ${status}${price}`, { sticky: true });
    layer.on("click", () => onSelect(name));
  };

  const hasRadius = !!centre && radiusMiles !== undefined;

  return (
    <div className={className}>
      {error && (
        <div className="mb-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
          Couldn't load postcode boundaries: {error}
        </div>
      )}
      <div className="relative h-full w-full overflow-hidden rounded-lg border border-gray-200">
        {!polygons && !error && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/70">
            <LoadingWheel />
          </div>
        )}
        <MapContainer center={centre ?? UK_CENTRE} zoom={centre ? 10 : 6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <MapTileLayer />
          {hasRadius ? <FitToRadius centre={centre!} radiusMiles={radiusMiles!} /> : <FitToPolygons polygons={polygons} />}
          {polygons &&
            Object.entries(polygons).map(([area, fc]) => (
              <GeoJSON
                key={`${area}-${styleKey}`}
                data={fc}
                style={style as L.StyleFunction}
                onEachFeature={onEachFeature as (feature: Feature, layer: L.Layer) => void}
              />
            ))}
          {hasRadius && (
            <Circle
              center={centre!}
              radius={radiusMiles! * MILES_TO_METRES}
              pathOptions={{ color: "#111827", weight: 1.5, dashArray: "6 4", fill: false }}
              interactive={false}
            />
          )}
          {centre && (
            <CircleMarker center={centre} radius={7} pathOptions={{ color: "#111827", fillColor: "#facc15", fillOpacity: 1 }}>
              <Tooltip permanent direction="top" offset={[0, -8]}>
                Your yard
              </Tooltip>
            </CircleMarker>
          )}
        </MapContainer>
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-blue-700 bg-blue-500/40" /> {legend?.covered ?? "Covered"}</span>
        {legend?.implicit && (
          <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-blue-700 bg-blue-400/20" /> {legend.implicit}</span>
        )}
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-dashed border-red-700 bg-red-500/20" /> {legend?.excluded ?? "De-selected"}</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-gray-400 bg-gray-200/40" /> {legend?.outside ?? "Outside radius"}</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border-2 border-violet-600" /> Unsaved change</span>
      </div>
    </div>
  );
}
